import { jwtDecode } from "jwt-decode";

export const BASE_API = window.BASE_API || "https://api.voipflow.us/";
export const BASE_WEB_SOCKET = window.BASE_WEB_SOCKET || "https://api.voipflow.us/";
export const API_SERVER_URL = window.API_SERVER_URL || window.BASE_API || "https://api.voipflow.us/";
export const BASE_DOMAIN = window.BASE_DOMAIN || "sip.voipflow.us";
export const VOIP_WS_URL = window.VOIP_WS_URL || "wss://sip.voipflow.us/ws";
export const FILE_BASE_RECORDING = window.FILE_BASE_RECORDING || "https://sip.voipflow.us/";
export const TOKEN_STORAGE_KEY = "psx_token";
export const TOKEN_LOGIN_AT_KEY = "psx_token_login_at";
export const TOKEN_MAX_AGE_MS = 60 * 60 * 1000;

function normalizeBaseUrl(baseUrl = "") {
  return String(baseUrl || "").replace(/\/+$/, "");
}

function normalizeRelativePath(path = "") {
  return String(path || "").replace(/^\/+/, "");
}

export function buildAbsoluteUrl(baseUrl = "", path = "") {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const normalizedPath = normalizeRelativePath(path);

  if (!normalizedBaseUrl) {
    return normalizedPath ? `/${normalizedPath}` : "/";
  }

  return normalizedPath
    ? `${normalizedBaseUrl}/${normalizedPath}`
    : normalizedBaseUrl;
}

export function buildApiUrl(path = "") {
  return buildAbsoluteUrl(API_SERVER_URL, path);
}

const isBrowser = typeof window !== "undefined";

const getStoredTokenValue = () => {
  if (!isBrowser) {
    return "";
  }

  return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
};

const decodeTokenSafely = (token) => {
  if (!token || token === "undefined") {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Invalid token:", error.message);
    return null;
  }
};

export const clearAuthSession = () => {
  if (!isBrowser) {
    return;
  }

  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_LOGIN_AT_KEY);
};

const getSessionLoginTimestamp = (decodedToken = {}) => {
  if (!isBrowser) {
    return 0;
  }

  const storedTimestamp = Number(localStorage.getItem(TOKEN_LOGIN_AT_KEY));
  if (Number.isFinite(storedTimestamp) && storedTimestamp > 0) {
    return storedTimestamp;
  }

  const issuedAtSeconds = Number(decodedToken?.iat);
  const fallbackTimestamp =
    Number.isFinite(issuedAtSeconds) && issuedAtSeconds > 0
      ? issuedAtSeconds * 1000
      : Date.now();

  localStorage.setItem(TOKEN_LOGIN_AT_KEY, String(fallbackTimestamp));
  return fallbackTimestamp;
};

const getSessionExpiryTimestamp = (token) => {
  if (!token || token === "undefined") {
    return 0;
  }

  const decodedToken = decodeTokenSafely(token);
  if (!decodedToken) {
    return 0;
  }

  const sessionExpiry = getSessionLoginTimestamp(decodedToken) + TOKEN_MAX_AGE_MS;

  const jwtExpirySeconds = Number(decodedToken?.exp);
  const jwtExpiry =
    Number.isFinite(jwtExpirySeconds) && jwtExpirySeconds > 0
      ? jwtExpirySeconds * 1000
      : Number.POSITIVE_INFINITY;

  return Math.min(sessionExpiry, jwtExpiry);
};

const redirectToLogin = () => {
  if (!isBrowser) {
    return;
  }

  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
};

export const hasActiveSession = () => {
  const token = getStoredTokenValue();

  if (!token || token === "undefined") {
    return false;
  }

  const expiryTimestamp = getSessionExpiryTimestamp(token);
  if (!expiryTimestamp || Date.now() >= expiryTimestamp) {
    clearAuthSession();
    return false;
  }

  return true;
};

export const storeAuthSession = (token) => {
  if (!isBrowser || !token) {
    return;
  }

  const decodedToken = decodeTokenSafely(token);
  const issuedAtSeconds = Number(decodedToken?.iat);
  const loginTimestamp =
    Number.isFinite(issuedAtSeconds) && issuedAtSeconds > 0
      ? issuedAtSeconds * 1000
      : Date.now();

  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(TOKEN_LOGIN_AT_KEY, String(loginTimestamp));
};

export const getActiveToken = () => {
  const token = getStoredTokenValue();

  if (!token || token === "undefined") {
    return "";
  }

  if (!hasActiveSession()) {
    redirectToLogin();
    return "";
  }

  return token;
};

export const buildAuthenticatedHeaders = (headers = {}) => {
  const token = getActiveToken();
  if (!token) {
    return { ...headers };
  }

  return {
    Authorization: "Bearer " + token,
    ...headers,
  };
};

export const buildAuthenticatedEventSourceUrl = (path = "", params = {}) => {
  const baseUrl = buildApiUrl(path);
  if (!baseUrl) {
    return null;
  }

  const url = new URL(baseUrl, window.location.origin);
  const token = getActiveToken();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });

  if (token) {
    url.searchParams.set("token", token);
  }

  return url.toString();
};

const handleUnauthorizedResponse = () => {
  clearAuthSession();
  redirectToLogin();
};

export const DecryptToken = () => {
  const token = getActiveToken();

  if (!token) {
    return {};
  }

  return decodeTokenSafely(token) || {};
};

export async function CallGETAPI(url, params = "", headers = {}) {
  try {
    let token = getActiveToken();
    let AllHeaders = { ...headers };
    if (token) {
      AllHeaders = { Authorization: "Bearer " + token, ...headers };
    }
    let res = await fetchWithTimeout(BASE_API + url + params, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...AllHeaders,
      },
    });

    if (res.status === 401 || res.status === 403) {
      handleUnauthorizedResponse();
      return { status: false, msg: "Unauthorized - Redirecting to login", data: [] };
    }

    let resultData = null;
    try {
      resultData = await res.json();
    } catch {
      // Proxies may return an HTML error page instead of API JSON.
    }
    if (!res.ok) {
      return {
        status: false,
        httpStatus: res.status,
        msg: resultData?.message || resultData?.msg || `Request failed with status ${res.status}`,
        data: resultData || {},
      };
    }
    return { status: true, msg: "", data: resultData };
  } catch (e) {
    return { status: false, msg: e.message, data: [] };
  }
}

export async function CallPOSTAPI(url, params = {}, headers = {}) {
  try {
    let token = getActiveToken();
    let AllHeaders = { ...headers };
    if (token) {
      AllHeaders = { Authorization: "Bearer " + token, ...headers };
    }

    const isFormData = params instanceof FormData;

    let res = await fetchWithTimeout(BASE_API + url, {
      method: "POST",
      headers: isFormData ? { ...AllHeaders } : { "Content-Type": "application/json", ...AllHeaders },
      body: isFormData ? params : JSON.stringify(params),
    });

    let resultData = null;
    const responseContentType = res.headers.get("content-type") || "";

    if (responseContentType.includes("application/json")) {
      resultData = await res.json();
    } else {
      const responseText = await res.text();
      resultData = responseText ? { message: responseText } : null;
    }

    if (res.status === 401 || res.status === 403) {
      if (token) {
        handleUnauthorizedResponse();
      }

      return {
        status: false,
        msg:
          resultData?.message ||
          resultData?.msg ||
          "Unauthorized - Redirecting to login",
        data: resultData || {},
      };
    }

    if (!res.ok) {
      return {
        status: false,
        msg:
          resultData?.message ||
          resultData?.msg ||
          `Request failed with status ${res.status}`,
        data: resultData || {},
      };
    }

    return {
      status: true,
      msg: resultData?.msg || resultData?.message || "",
      data: resultData,
    };
  } catch (e) {
    return { status: false, msg: e.message, data: [] };
  }
}

export async function CallPostFileUpload(url, params, headers = {}) {
  try {
    let token = getActiveToken();
    let AllHeaders = { ...headers };
    if (token) {
      AllHeaders = { Authorization: "Bearer " + token, ...headers };
    }

    let res = await fetchWithTimeout(BASE_API + url, {
      method: "POST",
      headers: AllHeaders,
      body: params,
    });

    if (res.status === 401 || res.status === 403) {
      handleUnauthorizedResponse();
      return { status: false, msg: "Unauthorized - Redirecting to login", data: [] };
    }

    if (!res.ok) {
      return { status: false, msg: `Request failed with status ${res.status}`, data: [] };
    }

    let resultData = await res.json();
    return { status: true, msg: "", data: resultData };
  } catch (e) {
    return { status: false, msg: e.message, data: [] };
  }
}

export async function CallDELETEAPI(url, headers = {}) {
  try {
    let token = getActiveToken();
    let AllHeaders = { ...headers };
    if (token) {
      AllHeaders = { Authorization: "Bearer " + token, ...headers };
    }

    const response = await fetchWithTimeout(BASE_API + url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...AllHeaders,
      },
    });

    if (response.status === 401 || response.status === 403) {
      handleUnauthorizedResponse();
      return { status: false, msg: "Unauthorized - Redirecting to login" };
    }

    if (!response.ok) {
      return { status: false, msg: `Request failed with status ${response.status}` };
    }

    return await response.json();
  } catch (error) {
    console.error("Error in DELETE API:", error);
    return { status: false, msg: error.message || "Error in DELETE request" };
  }
}

// Bound requests without conflating a network timeout with rejected credentials.
async function fetchWithTimeout(url, options = {}) {
  return fetch(url, { ...options, signal: options.signal || AbortSignal.timeout(15000) });
}
